// Quick guide — the Big 4 verbs (ser, estar, ter, ir), present tense. The
// most-used verbs in Portuguese, all irregular, worth knowing cold. Hand-drawn
// paper style. Pairs with the verb articles — read once, refer here forever.
import { speakButtonHTML } from "../lib/audio.js";

const PRONOUNS = ["eu", "tu", "ele / ela", "nós", "vós", "eles"];
const VERBS = [
  { inf: "ser", en: "to be — identity, traits", forms: ["sou", "és", "é", "somos", "sois", "são"], color: "text-emerald-700" },
  { inf: "estar", en: "to be — state, place", forms: ["estou", "estás", "está", "estamos", "estais", "estão"], color: "text-sky-700" },
  { inf: "ter", en: "to have", forms: ["tenho", "tens", "tem", "temos", "tendes", "têm"], color: "text-amber-700" },
  { inf: "ir", en: "to go", forms: ["vou", "vais", "vai", "vamos", "ides", "vão"], color: "text-rose-600" },
];

function colHTML(v) {
  const rows = v.forms
    .map(
      (f, i) => `
      <div class="flex items-baseline justify-between gap-2 py-0.5 border-b border-dashed border-amber-300/40 last:border-0">
        <span class="text-sage-400 text-sm" style="font-family:Inter,sans-serif">${PRONOUNS[i]}</span>
        <span class="text-sage-800 font-semibold">${f}${speakButtonHTML(f)}</span>
      </div>`
    )
    .join("");
  return `
    <div class="flex-1 min-w-[8.5rem]">
      <h3 class="${v.color} text-2xl sketch-underline inline-block leading-none">${v.inf}</h3>
      <p class="text-xs text-sage-500 mb-2 mt-1" style="font-family:Inter,sans-serif">${v.en}</p>
      ${rows}
    </div>`;
}

export function bigFourVerbsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">The Big 4 Verbs ⭐</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">The four you'll reach for constantly — all irregular, so learn them as whole shapes. Present tense.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        <div class="flex flex-wrap gap-5">${VERBS.map(colHTML).join("")}</div>
        <div class="mt-5 pt-4 border-t border-dashed border-amber-300/50 text-base">
          <p class="text-amber-700 text-xl sketch-underline inline-block mb-1">ser vs estar</p>
          <p class="text-sage-700">Both mean “to be”. <b class="text-emerald-700">ser</b> = who/what something <i>is</i> (identity, origin, lasting traits). <b class="text-sky-700">estar</b> = how/where it <i>is right now</i> (location, mood, temporary state). <span class="text-sage-500">sou alto</span> (I'm tall) vs <span class="text-sage-500">estou cansado</span> (I'm tired). For the progressive, European Portuguese uses <span class="text-sage-500">estar a + infinitive</span> — <span class="text-sage-500">estou a comer</span> (I'm eating).</p>
          <p class="text-sage-600 mt-2 text-sm" style="font-family:Inter,sans-serif">Bonus: <b class="text-amber-700">ter</b> is the everyday “to have”; its old cousin <b>haver</b> now lives mostly as impersonal <b>há</b> = “there is / there are”.</p>
        </div>
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Want the full conjugation across every tense? Open any of these from the Verb Index.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
